import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Package,
  Layers,
  Box,
  Search,
  AlertTriangle,
  Truck,
  ScanLine,
  Loader2,
  BoxSelect
} from 'lucide-react';
import inventoryService, { Lot, LotScanDetail, GroupedFinishedProduct, InventoryTransaction, BoxSearchResult, ByproductBarcodeResult } from '../../services/inventoryService';
import { parseGS1128Barcode } from '../../utils/barcodeUtils';
import { displayRemainingCases } from '../../utils/lotCases';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDashboardWebSocket } from '../../hooks/useDashboardWebSocket';
import { formatYield } from '../../hooks/useYieldFactor';
import productionService from '../../services/productionService';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import LotDetailModal from './components/LotDetailModal';
import FinishedProductHistoryModal from './components/FinishedProductHistoryModal';
import PackingMaterialsTab from './PackingMaterialsTab';
import BoxSearchTab from './BoxSearchTab';
import { getStatusBadge, getLifecycleDisplayName, getLotTypeBadge, getLotTypeDisplayName } from './inventoryHelpers';
import { lotDisplayType, lotLifecycle } from '@tnb/lot-status';
import FreshnessBadge from '../../components/FreshnessBadge';

type TabType = 'lots' | 'finished' | 'boxLookup' | 'boxSearch' | 'packingMaterials';

const Dashboard: React.FC = () => {
  const { t } = useLanguage();
  usePageTitle(t('inventory.dashboard'), t('nav.inventory'));
  const queryClient = useQueryClient();

  // Real-time WebSocket updates for inventory data
  useDashboardWebSocket({
    enableProductionEvents: false,  // Not needed for inventory dashboard
    enableWorkforceEvents: false,   // Not needed for inventory dashboard
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabType) || 'lots';
  const [summaryCollapsed, setSummaryCollapsed] = useState(activeTab === 'boxLookup');
  const handleTabClick = (tab: TabType) => {
    if (tab === activeTab) {
      setSummaryCollapsed(prev => !prev);
    } else {
      setSearchParams({ tab }, { replace: true });
    }
    if (tab === 'boxLookup') {
      setTimeout(() => barcodeInputRef.current?.focus(), 100);
    }
  };
  const [lotsSearch, setLotsSearch] = useState('');
  const [lotsLifecycle, setLotsLifecycle] = useState('');
  const [lotsType, setLotsType] = useState('');
  const [lotsPage, setLotsPage] = useState(1);
  const [finishedSearch, setFinishedSearch] = useState('');
  const [finishedStatus, setFinishedStatus] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [boxLookupResult, setBoxLookupResult] = useState<BoxSearchResult | null>(null);
  const [bufferLookupResult, setBufferLookupResult] = useState<{ result_type: 'buffer' | 'case'; row: ByproductBarcodeResult } | null>(null);
  const [boxLookupLoading, setBoxLookupLoading] = useState(false);
  const [boxLookupError, setBoxLookupError] = useState<string | null>(null);
  const [rawMaterialResult, setRawMaterialResult] = useState<{
    item_number: string;
    item_name?: string;
    gtin: string;
    lot?: string;
    weight?: number;
    brand?: string;
    chinese_brand?: string;
    yield_factor?: number;
  } | null>(null);
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  // Auto-focus barcode input when navigating directly to boxLookup (e.g. from Label Lookup)
  useEffect(() => {
    if (activeTab === 'boxLookup') {
      setTimeout(() => barcodeInputRef.current?.focus(), 200);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Lot detail modal state (combined history + scan details)
  const [scanDetailsLot, setScanDetailsLot] = useState<Lot | null>(null);
  const [scanDetails, setScanDetails] = useState<LotScanDetail[]>([]);
  const [scanDetailsLoading, setScanDetailsLoading] = useState(false);
  const [txModalData, setTxModalData] = useState<InventoryTransaction[]>([]);
  const [txModalLoading, setTxModalLoading] = useState(false);
  const [txModalPage, setTxModalPage] = useState(1);
  const [txModalTotal, setTxModalTotal] = useState(0);
  const [txModalTotalPages, setTxModalTotalPages] = useState(0);

  // Finished product detail modal state (history only)
  const [txModalItem, setTxModalItem] = useState<{
    item_number: string;
    item_name: string;
    chinese_name?: string;
    is_byproduct_marketable?: boolean;
  } | null>(null);

  // Fetch lot summary stats
  const { data: lotsSummary, isLoading: lotsSummaryLoading } = useQuery(
    'lots-summary',
    () => inventoryService.getLotsSummary(),
    { refetchInterval: 30000 }
  );

  // Fetch finished products summary stats
  const { data: finishedSummary, isLoading: finishedSummaryLoading } = useQuery(
    'finished-products-summary',
    () => inventoryService.getFinishedProductsSummary(),
    { refetchInterval: 30000 }
  );

  // Fetch lots data
  const { data: lotsData, isLoading: lotsLoading } = useQuery(
    ['lots', lotsSearch, lotsLifecycle, lotsType, lotsPage],
    () => inventoryService.getLots({
      page: lotsPage,
      limit: 20,
      search: lotsSearch || undefined,
      lifecycle: lotsLifecycle || undefined,
      lot_type: lotsType || undefined,
      exclude_fp_ni: 'true',
    }),
    { refetchInterval: 30000, keepPreviousData: true }
  );

  // Fetch finished products grouped by item_number
  const { data: finishedGroupedData, isLoading: finishedLoading } = useQuery(
    ['finished-products-grouped', finishedSearch, finishedStatus],
    () => inventoryService.getFinishedProductsGrouped({
      search: finishedSearch || undefined,
      status: finishedStatus || undefined
    }),
    { refetchInterval: 30000 }
  );

  // Use centralized date utilities
  const formatDate = (dateString?: string) => formatDateUtil(dateString);
  const formatDateTime = (dateString?: string | null) => formatDateTimeUtil(dateString);

  // getStatusBadge, getLifecycleDisplayName, getLotType* imported from inventoryHelpers.ts

  // Open lot detail modal (history + scan details)
  const openScanDetails = async (lot: Lot) => {
    setScanDetailsLot(lot);
    setScanDetails([]);
    setScanDetailsLoading(true);
    setTxModalData([]);
    setTxModalLoading(true);
    setTxModalPage(1);

    // Fetch both in parallel
    try {
      const [details, txResult] = await Promise.all([
        inventoryService.getLotScanDetails(lot.id),
        inventoryService.getInventoryTransactions({ page: 1, limit: 20, item_number: lot.item_number, lot_id: lot.id })
      ]);
      setScanDetails(details);
      setTxModalData(txResult.transactions);
      setTxModalTotal(txResult.pagination.total);
      setTxModalTotalPages(txResult.pagination.total_pages);
    } catch {
      setScanDetails([]);
      setTxModalData([]);
    } finally {
      setScanDetailsLoading(false);
      setTxModalLoading(false);
    }
  };

  // Paginate transaction history within the lot modal
  const loadLotTxPage = async (page: number) => {
    if (!scanDetailsLot) return;
    setTxModalLoading(true);
    setTxModalPage(page);
    try {
      const result = await inventoryService.getInventoryTransactions({ page, limit: 20, item_number: scanDetailsLot.item_number, lot_id: scanDetailsLot.id });
      setTxModalData(result.transactions);
      setTxModalTotal(result.pagination.total);
      setTxModalTotalPages(result.pagination.total_pages);
    } catch {
      setTxModalData([]);
    } finally {
      setTxModalLoading(false);
    }
  };

  // Open transaction history modal for finished products
  const openTxHistory = async (
    item_number: string,
    item_name: string,
    chinese_name?: string,
    is_byproduct_marketable?: boolean,
    page = 1
  ) => {
    setTxModalItem({ item_number, item_name, chinese_name, is_byproduct_marketable });
    setTxModalLoading(true);
    setTxModalPage(page);
    try {
      const result = await inventoryService.getInventoryTransactions({ page, limit: 20, item_number });
      setTxModalData(result.transactions);
      setTxModalTotal(result.pagination.total);
      setTxModalTotalPages(result.pagination.total_pages);
    } catch {
      setTxModalData([]);
    } finally {
      setTxModalLoading(false);
    }
  };

  const closeTxHistory = () => {
    setTxModalItem(null);
    setTxModalData([]);
    setTxModalPage(1);
  };

  const handleBarcodeLookup = async () => {
    const barcode = barcodeInput.trim();
    if (!barcode) return;
    setBoxLookupLoading(true);
    setBoxLookupError(null);
    setBoxLookupResult(null);
    setBufferLookupResult(null);
    setRawMaterialResult(null);
    try {
      // Try GS1-128 barcode — resolve GTIN but don't short-circuit yet; the barcode
      // may belong to a by-product buffer/case whose GTIN also lives in item_to_upc.
      let pendingRawMaterial: typeof rawMaterialResult = null;
      const parsed = parseGS1128Barcode(barcode);
      if (parsed?.gtin) {
        const gtinResult = await inventoryService.lookupGtin(parsed.gtin);
        if (gtinResult) {
          let yieldFactor: number | undefined;
          try {
            const yieldData = await productionService.getYieldFactor(gtinResult.item_number);
            yieldFactor = yieldData.yield_factor ?? undefined;
          } catch { /* use undefined if yield lookup fails */ }
          pendingRawMaterial = {
            item_number: gtinResult.item_number,
            item_name: gtinResult.item_name,
            gtin: parsed.gtin,
            lot: parsed.lot,
            weight: parsed.normalizedWeightLb ?? gtinResult.unit_weight,
            brand: gtinResult.brand,
            chinese_brand: gtinResult.chinese_brand,
            yield_factor: yieldFactor
          };
        }
      }

      // Box search (also resolves marketable buffer/case by-product labels).
      // Buffer/case results take priority over a GTIN raw-material match.
      const result = await inventoryService.searchBoxes({ barcode, limit: 1 });
      if (result.result_type === 'buffer' && result.buffer) {
        setBufferLookupResult({ result_type: 'buffer', row: result.buffer });
      } else if (result.result_type === 'case' && result.case) {
        setBufferLookupResult({ result_type: 'case', row: result.case });
      } else if (result.boxes && result.boxes.length > 0) {
        setBoxLookupResult(result.boxes[0]);
      } else if (pendingRawMaterial) {
        setRawMaterialResult(pendingRawMaterial);
      } else {
        setBoxLookupError('not_found');
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setBoxLookupError('not_found');
      } else {
        setBoxLookupError('error');
      }
    } finally {
      setBoxLookupLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards — 2 per row on mobile, 4 on desktop; collapses when a tab is active */}
      {!summaryCollapsed && <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Raw Material Lots Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('inventory.availableLots')}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {lotsSummaryLoading ? '...' : (lotsSummary?.available_lots || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {lotsSummaryLoading ? '...' : `${Number(lotsSummary?.total_available_weight || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs`}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Layers className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        {/* Expiring Soon Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('inventory.expiringSoon')}</p>
              <p className="text-xl sm:text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                {lotsSummaryLoading ? '...' : (lotsSummary?.expiring_soon_lots || 0)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('inventory.within7Days')}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </div>

        {/* Finished Products Card — WCO-3272: on-hand = packed − DTO shipped */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('inventory.onHandFinished')}</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                {finishedSummaryLoading ? '...' : (finishedSummary?.on_hand_boxes ?? finishedSummary?.packed_boxes ?? 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {finishedSummaryLoading ? '...' : `${Number(finishedSummary?.on_hand_weight ?? finishedSummary?.packed_weight ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs`}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Box className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        {/* Today's Production Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('inventory.boxesToday')}</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                {finishedSummaryLoading ? '...' : (finishedSummary?.boxes_today || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {finishedSummaryLoading ? '...' : `${(finishedSummary?.shipped_boxes || 0).toLocaleString()} ${t('inventory.dtoShipped')}`}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>}

      {/* Tab Buttons */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => handleTabClick('lots')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'lots'
            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
        >
          <Layers className="w-4 h-4 inline-block mr-1 -mt-0.5" />
          {t('inventory.rawMaterialLots')}
        </button>
        <button
          onClick={() => handleTabClick('finished')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'finished'
            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
        >
          <Package className="w-4 h-4 inline-block mr-1 -mt-0.5" />
          {t('inventory.finishedProducts')}
        </button>
        <button
          onClick={() => handleTabClick('packingMaterials')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'packingMaterials'
            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
        >
          <BoxSelect className="w-4 h-4 inline-block mr-1 -mt-0.5" />
          {t('inventory.packingMaterials')}
        </button>
        <button
          onClick={() => handleTabClick('boxLookup')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'boxLookup'
            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
        >
          <ScanLine className="w-4 h-4 inline-block mr-1 -mt-0.5" />
          {t('inventory.boxLookup')}
        </button>
        <button
          onClick={() => handleTabClick('boxSearch')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'boxSearch'
            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
        >
          <Search className="w-4 h-4 inline-block mr-1 -mt-0.5" />
          {t('boxSearch.title')}
        </button>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'lots' && (
          <>
            {/* Lots Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder={t('common.search')}
                  value={lotsSearch}
                  onChange={(e) => { setLotsSearch(e.target.value); setLotsPage(1); }}
                  className="w-full pl-10 pr-4 py-2 border border-secondary-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <select
                value={lotsLifecycle}
                onChange={(e) => { setLotsLifecycle(e.target.value); setLotsPage(1); }}
                className="px-4 py-2 border border-secondary-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{t('inventory.allLifecycleStatuses')}</option>
                <option value="available">{t('inventory.statusAvailable')}</option>
                <option value="on_hold_approved">{t('inventory.onHoldApproved') || 'On hold (approved)'}</option>
                <option value="on_hold_pending">{t('inventory.onHoldPending') || 'On hold (pending)'}</option>
                <option value="rejected">{t('inventory.statusRejected') || 'Rejected'}</option>
                <option value="consumed">{t('inventory.statusConsumed') || 'Consumed'}</option>
              </select>
              <select
                value={lotsType}
                onChange={(e) => { setLotsType(e.target.value); setLotsPage(1); }}
                className="px-4 py-2 border border-secondary-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{t('inventory.allLotTypes')}</option>
                <option value="intact">{t('inventory.lotTypeIntact')}</option>
                <option value="not_intact">{t('inventory.lotTypeNotIntact')}</option>
                <option value="by_product_m">{t('inventory.lotTypeByProductM')}</option>
                <option value="by_product_p">{t('inventory.lotTypeByProductP')}</option>
              </select>
            </div>

            {/* Lots Table */}
            {lotsLoading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
            ) : lotsData && lotsData.lots.length > 0 ? (
              <>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.itemNumber')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.itemName')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.lotNumber')}</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.quantity')}</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.currentWeight')}</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('common.status')}</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.lotType')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.updatedAt')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.updatedBy')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {lotsData.lots.map((lot: Lot, index: number) => {
                          const lifecycle = lotLifecycle(lot);
                          const displayType = lotDisplayType(lot);
                          return (
                          <tr key={`${lot.id}-${lot.transfer_number}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => openScanDetails(lot)}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {lot.item_number}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              <div>{lot.item_name || '-'}</div>
                              {lot.chinese_name && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">{lot.chinese_name}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                              {lot.transfer_number}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300">
                              {/* WCO-2594: remaining cases derived from live weight (packing-aware); '—' when no basis */}
                              {(() => { const c = displayRemainingCases(lot); return c != null ? c.toLocaleString() : '—'; })()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-300">
                              <div>{Number(lot.current_amount || lot.actual_weight || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs</div>
                              {lot.allocated_session && (
                                <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                  → {t('inventory.allocatedToSession')} {lot.allocated_session} ({Number(lot.allocated_weight || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs)
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(lifecycle, lot.approval_status)}`}>
                                  {getLifecycleDisplayName(lifecycle, t, lot.approval_status)}
                                </span>
                                {displayType === 'by_product_p' && lot.freshness_badge_key && (
                                  <FreshnessBadge
                                    ageDays={lot.freshness_age_days}
                                    badgeKey={lot.freshness_badge_key}
                                    badgeLabel={lot.freshness_badge_label}
                                    badgeColor={lot.freshness_badge_color}
                                  />
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getLotTypeBadge(displayType, lot.is_ni_remnant)}`}>
                                {getLotTypeDisplayName(displayType, t, lot.is_ni_remnant)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                              {lot.verified_at ? formatDateTime(lot.verified_at) : lot.updated_at ? formatDateTime(lot.updated_at) : formatDate(lot.received_date)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                              {lot.received_by_name || lot.verified_by_name || '-'}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {lotsData.pagination.total_pages > 1 && (
                    <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t('common.showing')} {((lotsPage - 1) * 20) + 1} - {Math.min(lotsPage * 20, lotsData.pagination.total)} {t('common.of')} {lotsData.pagination.total}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setLotsPage(p => Math.max(1, p - 1))}
                          disabled={lotsPage === 1}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 dark:bg-gray-800"
                        >
                          {t('common.previous')}
                        </button>
                        <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300">
                          {lotsPage} / {lotsData.pagination.total_pages}
                        </span>
                        <button
                          onClick={() => setLotsPage(p => Math.min(lotsData.pagination.total_pages, p + 1))}
                          disabled={lotsPage === lotsData.pagination.total_pages}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 dark:bg-gray-800"
                        >
                          {t('common.next')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('messages.noData')}</div>
            )}
          </>
        )}

        {activeTab === 'finished' && (
          <>
            {/* Finished Products Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder={t('common.search')}
                  value={finishedSearch}
                  onChange={(e) => setFinishedSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-secondary-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <select
                value={finishedStatus}
                onChange={(e) => setFinishedStatus(e.target.value)}
                className="px-4 py-2 border border-secondary-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{t('inventory.allStatuses')}</option>
                <option value="on_hand">{t('inventory.onHandFinished')}</option>
                <option value="packed">{t('inventory.statusPacked')}</option>
                <option value="shipped">{t('inventory.statusShipped')}</option>
              </select>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              {t('inventory.finishedProductsReconciliationNote')}
            </p>

            {/* Finished Products Table - Grouped by Item */}
            {finishedLoading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
            ) : finishedGroupedData && finishedGroupedData.length > 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.itemNumber')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.productName')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          {t('inventory.onHandCases')}
                          <span className="block text-[10px] normal-case tracking-normal text-gray-400 dark:text-gray-500 font-normal">{t('inventory.onHandCasesNote')}</span>
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.expectedCount')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.onHandWeight')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.statusPacked')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.dtoShipped')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.fpNiOnHand')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.lastPacked')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {finishedGroupedData.map((item: GroupedFinishedProduct) => (
                        <tr
                          key={item.item_number}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                          onClick={() => openTxHistory(
                            item.item_number,
                            item.product_name || '',
                            item.chinese_name,
                            item.is_byproduct_marketable
                          )}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {item.item_number}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            <div className="flex flex-wrap items-center gap-2">
                              <span>{item.product_name || '-'}</span>
                              {item.is_byproduct_marketable && (
                                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                  {t('inventory.marketableByproduct')}
                                </span>
                              )}
                              {item.is_fp_ni_only && (
                                <span className="inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800 dark:bg-violet-900/40 dark:text-violet-300">
                                  {t('inventory.lotTypeFpNi')}
                                </span>
                              )}
                              {item.has_fp_ni && !item.is_fp_ni_only && (
                                <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-200">
                                  {t('inventory.includesFpNi')}
                                </span>
                              )}
                            </div>
                            {item.chinese_name && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">{item.chinese_name}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                            {Number(item.on_hand_count ?? item.box_count ?? 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-300">
                            {Number(item.expected_count || 0) > 0 ? (
                              <span className={Number(item.packed_count) >= Number(item.expected_count) ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}>
                                {Number(item.expected_count).toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-300">
                            {Number(item.on_hand_weight ?? item.total_weight ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600 dark:text-blue-400">
                            {Number(item.packed_count).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-purple-600 dark:text-purple-400">
                            {Number(item.shipped_count).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-violet-600 dark:text-violet-400">
                            {Number(item.fp_ni_on_hand_weight || 0) > 0
                              ? `${Number(item.fp_ni_on_hand_weight).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs`
                              : '—'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {formatDateTime(item.last_packed_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('messages.noData')}</div>
            )}
          </>
        )}

        {activeTab === 'boxLookup' && (
          <>
            {/* Barcode Search */}
            <div className="flex gap-3 mb-6">
              <div className="relative flex-1 max-w-lg">
                <ScanLine className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
                <input
                  ref={barcodeInputRef}
                  type="text"
                  placeholder={t('inventory.boxLookupSearchPlaceholder')}
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleBarcodeLookup();
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-secondary-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                onClick={handleBarcodeLookup}
                disabled={boxLookupLoading || !barcodeInput.trim()}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {boxLookupLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                {t('inventory.boxLookupSearch')}
              </button>
            </div>

            {/* Error Message */}
            {boxLookupError === 'not_found' && (
              <div className="text-center py-8 text-secondary-500 dark:text-gray-400">
                <ScanLine className="w-12 h-12 mx-auto mb-3 text-secondary-300 dark:text-gray-600" />
                <p>{t('inventory.boxLookupNoResults')}</p>
              </div>
            )}
            {boxLookupError === 'error' && (
              <div className="text-center py-8 text-red-500 dark:text-red-400">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3" />
                <p>{t('inventory.boxLookupError')}</p>
              </div>
            )}

            {/* Raw Material Results Card */}
            {rawMaterialResult && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    {rawMaterialResult.item_number} - {rawMaterialResult.item_name || t('inventory.boxLookupNotAvailable')}
                  </h3>
                  <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                    {t('inventory.rawMaterialFound')}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {/* Item Number */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.rawMaterialItemNumber')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">{rawMaterialResult.item_number}</p>
                  </div>

                  {/* GTIN */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.rawMaterialGtin')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1 font-mono">{rawMaterialResult.gtin}</p>
                  </div>

                  {/* Lot Number */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.rawMaterialLot')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">{rawMaterialResult.lot || t('inventory.boxLookupNotAvailable')}</p>
                  </div>

                  {/* Scanned Weight */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.rawMaterialWeight')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">
                      {rawMaterialResult.weight ? `${rawMaterialResult.weight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs` : t('inventory.boxLookupNotAvailable')}
                    </p>
                  </div>

                  {/* Brand */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.rawMaterialBrand')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">{rawMaterialResult.brand || t('inventory.boxLookupNotAvailable')}</p>
                    {rawMaterialResult.chinese_brand && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{rawMaterialResult.chinese_brand}</p>
                    )}
                  </div>

                  {/* Yield Factor */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.rawMaterialYieldFactor')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">
                      {rawMaterialResult.yield_factor ? formatYield(rawMaterialResult.yield_factor) : t('inventory.boxLookupNotAvailable')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Finished Product Box Results Card */}
            {boxLookupResult && (
              <div className="bg-secondary-50 dark:bg-gray-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    {boxLookupResult.item_number} - {boxLookupResult.product_name || t('inventory.boxLookupNotAvailable')}
                    {boxLookupResult.chinese_name && ` (${boxLookupResult.chinese_name})`}
                  </h3>
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(boxLookupResult.status)}`}>
                    {boxLookupResult.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Session */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.boxLookupSession')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">{boxLookupResult.session_number || t('inventory.boxLookupNotAvailable')}</p>
                  </div>

                  {/* Station */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.boxLookupStation')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">{boxLookupResult.station_name || t('inventory.boxLookupNotAvailable')}</p>
                  </div>

                  {/* Operator */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">
                      {t('inventory.boxLookupOperator')}
                    </p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">
                      {boxLookupResult.operator_employee_number
                        ? `${boxLookupResult.operator_employee_number}${boxLookupResult.operator_name ? ` - ${boxLookupResult.operator_name}` : ''}`
                        : t('inventory.boxLookupNotAvailable')}
                    </p>
                  </div>

                  {/* Packer */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">
                      {t('inventory.boxLookupPacker')}
                    </p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">
                      {boxLookupResult.packer_employee_number
                        ? `${boxLookupResult.packer_employee_number}${boxLookupResult.packer_name ? ` - ${boxLookupResult.packer_name}` : ''}`
                        : t('inventory.boxLookupNotAvailable')}
                    </p>
                  </div>

                  {/* Raw Material Item Number */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.boxLookupRawMaterial')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">{boxLookupResult.raw_material_item_number || t('inventory.boxLookupNotAvailable')}</p>
                  </div>

                  {/* Weight */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.boxLookupWeight')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">{Number(boxLookupResult.actual_weight).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs</p>
                  </div>

                  {/* Box Number */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.boxLookupBoxNumber')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">{boxLookupResult.box_number}</p>
                  </div>

                  {/* Packed At */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.boxLookupPackedAt')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">
                      {formatDateTime(boxLookupResult.packed_at)}
                    </p>
                  </div>

                </div>
              </div>
            )}

            {/* Marketable By-Product (buffer / case) Results Card */}
            {bufferLookupResult && (
              <div className="bg-secondary-50 dark:bg-gray-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    {bufferLookupResult.row.byproduct_item_number} - {bufferLookupResult.row.item_name || t('inventory.boxLookupNotAvailable')}
                    {bufferLookupResult.row.chinese_name && ` (${bufferLookupResult.row.chinese_name})`}
                  </h3>
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(bufferLookupResult.row.status)}`}>
                    {bufferLookupResult.row.status}
                  </span>
                </div>

                <p className="text-sm font-medium text-secondary-600 dark:text-gray-300 mb-4">
                  {bufferLookupResult.result_type === 'buffer' ? t('inventory.bufferFound') : t('inventory.caseFound')}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Source Material */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.bufferSourceMaterial')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">{bufferLookupResult.row.source_material_item_number || t('inventory.boxLookupNotAvailable')}</p>
                  </div>

                  {/* Weight */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.bufferReservedWeight')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">
                      {(() => {
                        const w = bufferLookupResult.result_type === 'buffer' ? bufferLookupResult.row.reserved_weight : bufferLookupResult.row.case_weight;
                        return w != null ? `${Number(w).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs` : t('inventory.boxLookupNotAvailable');
                      })()}
                    </p>
                  </div>

                  {/* Session */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.boxLookupSession')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">
                      {(bufferLookupResult.result_type === 'buffer' ? bufferLookupResult.row.last_session_number : bufferLookupResult.row.session_number) || t('inventory.boxLookupNotAvailable')}
                    </p>
                  </div>

                  {/* Sub-Session */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.bufferSubSession')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">
                      {(bufferLookupResult.result_type === 'buffer' ? bufferLookupResult.row.last_sub_session_number : bufferLookupResult.row.sub_session_number) || t('inventory.boxLookupNotAvailable')}
                    </p>
                  </div>

                  {/* Production Date */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.bufferProductionDate')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1">
                      {formatDate(bufferLookupResult.result_type === 'buffer' ? bufferLookupResult.row.last_production_date : bufferLookupResult.row.production_date) || t('inventory.boxLookupNotAvailable')}
                    </p>
                  </div>

                  {/* Barcode */}
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-gray-400 uppercase">{t('inventory.boxLookupBarcode')}</p>
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white mt-1 font-mono break-all">{bufferLookupResult.row.barcode_number || t('inventory.boxLookupNotAvailable')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Initial State - No search yet */}
            {!boxLookupResult && !bufferLookupResult && !rawMaterialResult && !boxLookupError && !boxLookupLoading && (
              <div className="text-center py-12 text-secondary-400 dark:text-gray-500">
                <ScanLine className="w-16 h-16 mx-auto mb-4" />
                <p className="text-lg">{t('inventory.boxLookupSearchPlaceholder')}</p>
              </div>
            )}
          </>
        )}

        {/* Box Search Tab */}
        {activeTab === 'boxSearch' && (
          <BoxSearchTab />
        )}

        {/* Packing Materials Tab */}
        {activeTab === 'packingMaterials' && (
          <PackingMaterialsTab />
        )}
      </div>

      {/* Lot Detail Modal (History + Scan Details tabs) */}
      {scanDetailsLot && (
        <LotDetailModal
          lot={scanDetailsLot}
          scanDetails={scanDetails}
          scanDetailsLoading={scanDetailsLoading}
          txData={txModalData}
          txLoading={txModalLoading}
          txPage={txModalPage}
          txTotal={txModalTotal}
          txTotalPages={txModalTotalPages}
          onLoadTxPage={loadLotTxPage}
          onClose={() => setScanDetailsLot(null)}
        />
      )}

      {/* Finished Product History Modal */}
      {txModalItem && (
        <FinishedProductHistoryModal
          item={txModalItem}
          txData={txModalData}
          txLoading={txModalLoading}
          txPage={txModalPage}
          txTotal={txModalTotal}
          txTotalPages={txModalTotalPages}
          onLoadPage={(page) => openTxHistory(
            txModalItem.item_number,
            txModalItem.item_name,
            txModalItem.chinese_name,
            txModalItem.is_byproduct_marketable,
            page
          )}
          onClose={closeTxHistory}
        />
      )}

    </div>
  );
};

export default Dashboard;
